import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { createAuthenticatedClient } from '@/lib/graphql';
import { useAuth } from '@/lib/auth/AuthContext';

// GraphQL queries and mutations
const FAVORITES_QUERY = gql`
  query FavoritesQuery {
    followedTopics(first: 100) {
      edges {
        node {
          slug
        }
      }
    }
    likedEpisodes(first: 100) {
      edges {
        node {
          topicSlug
          episodeId
        }
      }
    }
  }
`;

const FOLLOW_TOPIC_MUTATION = gql`
  mutation FollowTopic($input: FollowTopicInput!) {
    followTopic(input: $input) {
      topic {
        slug
        isFollowedByViewer
      }
      userErrors {
        message
      }
    }
  }
`;

const UNFOLLOW_TOPIC_MUTATION = gql`
  mutation UnfollowTopic($input: UnfollowTopicInput!) {
    unfollowTopic(input: $input) {
      topic {
        slug
        isFollowedByViewer
      }
      userErrors {
        message
      }
    }
  }
`;

const LIKE_EPISODE_MUTATION = gql`
  mutation LikeEpisode($input: LikeEpisodeInput!) {
    likeEpisode(input: $input) {
      episode {
        topicSlug
        episodeId
        isLikedByViewer
      }
      userErrors {
        message
      }
    }
  }
`;

const UNLIKE_EPISODE_MUTATION = gql`
  mutation UnlikeEpisode($input: UnlikeEpisodeInput!) {
    unlikeEpisode(input: $input) {
      episode {
        topicSlug
        episodeId
        isLikedByViewer
      }
      userErrors {
        message
      }
    }
  }
`;

interface FavoritesData {
  followedTopics: {
    edges: Array<{
      node: {
        slug: string;
      };
    }>;
  };
  likedEpisodes: {
    edges: Array<{
      node: {
        topicSlug: string;
        episodeId: string;
      };
    }>;
  };
}

type EpisodeKey = `${string}:${string}`;

function makeEpisodeKey(topicSlug: string, episodeId: string): EpisodeKey {
  return `${topicSlug}:${episodeId}`;
}

export function useFavorites() {
  const { isAuthenticated, accessToken } = useAuth();
  const queryClient = useQueryClient();

  // Local optimistic state for immediate UI feedback
  const [optimisticFollowedTopics, setOptimisticFollowedTopics] = useState<Set<string>>(new Set());
  const [optimisticLikedEpisodes, setOptimisticLikedEpisodes] = useState<Set<EpisodeKey>>(new Set());
  const [pendingTopics, setPendingTopics] = useState<Set<string>>(new Set());
  const [pendingEpisodes, setPendingEpisodes] = useState<Set<EpisodeKey>>(new Set());

  // Fetch favorites when authenticated
  const { data, isLoading } = useQuery({
    queryKey: ['favorites'],
    queryFn: async () => {
      if (!accessToken) return null;
      const client = createAuthenticatedClient(accessToken);
      return client.request<FavoritesData>(FAVORITES_QUERY);
    },
    enabled: isAuthenticated && !!accessToken,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Sync server data with optimistic state
  useEffect(() => {
    if (data) {
      const followedSlugs = new Set(data.followedTopics.edges.map((e) => e.node.slug));
      const likedKeys = new Set(
        data.likedEpisodes.edges.map((e) => makeEpisodeKey(e.node.topicSlug, e.node.episodeId))
      );
      setOptimisticFollowedTopics(followedSlugs);
      setOptimisticLikedEpisodes(likedKeys);
    }
  }, [data]);

  // Follow topic mutation
  const followMutation = useMutation({
    mutationFn: async (topicSlug: string) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      return client.request(FOLLOW_TOPIC_MUTATION, { input: { topicSlug } });
    },
    onMutate: (topicSlug) => {
      setPendingTopics((prev) => new Set(prev).add(topicSlug));
      setOptimisticFollowedTopics((prev) => new Set(prev).add(topicSlug));
    },
    onSettled: (_, __, topicSlug) => {
      setPendingTopics((prev) => {
        const next = new Set(prev);
        next.delete(topicSlug);
        return next;
      });
    },
    onError: (_, topicSlug) => {
      setOptimisticFollowedTopics((prev) => {
        const next = new Set(prev);
        next.delete(topicSlug);
        return next;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  // Unfollow topic mutation
  const unfollowMutation = useMutation({
    mutationFn: async (topicSlug: string) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      return client.request(UNFOLLOW_TOPIC_MUTATION, { input: { topicSlug } });
    },
    onMutate: (topicSlug) => {
      setPendingTopics((prev) => new Set(prev).add(topicSlug));
      setOptimisticFollowedTopics((prev) => {
        const next = new Set(prev);
        next.delete(topicSlug);
        return next;
      });
    },
    onSettled: (_, __, topicSlug) => {
      setPendingTopics((prev) => {
        const next = new Set(prev);
        next.delete(topicSlug);
        return next;
      });
    },
    onError: (_, topicSlug) => {
      setOptimisticFollowedTopics((prev) => new Set(prev).add(topicSlug));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  // Like episode mutation
  const likeMutation = useMutation({
    mutationFn: async ({ topicSlug, episodeId }: { topicSlug: string; episodeId: string }) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      return client.request(LIKE_EPISODE_MUTATION, { input: { topicSlug, episodeId } });
    },
    onMutate: ({ topicSlug, episodeId }) => {
      const key = makeEpisodeKey(topicSlug, episodeId);
      setPendingEpisodes((prev) => new Set(prev).add(key));
      setOptimisticLikedEpisodes((prev) => new Set(prev).add(key));
    },
    onSettled: (_, __, { topicSlug, episodeId }) => {
      const key = makeEpisodeKey(topicSlug, episodeId);
      setPendingEpisodes((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    },
    onError: (_, { topicSlug, episodeId }) => {
      const key = makeEpisodeKey(topicSlug, episodeId);
      setOptimisticLikedEpisodes((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  // Unlike episode mutation
  const unlikeMutation = useMutation({
    mutationFn: async ({ topicSlug, episodeId }: { topicSlug: string; episodeId: string }) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      return client.request(UNLIKE_EPISODE_MUTATION, { input: { topicSlug, episodeId } });
    },
    onMutate: ({ topicSlug, episodeId }) => {
      const key = makeEpisodeKey(topicSlug, episodeId);
      setPendingEpisodes((prev) => new Set(prev).add(key));
      setOptimisticLikedEpisodes((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    },
    onSettled: (_, __, { topicSlug, episodeId }) => {
      const key = makeEpisodeKey(topicSlug, episodeId);
      setPendingEpisodes((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    },
    onError: (_, { topicSlug, episodeId }) => {
      const key = makeEpisodeKey(topicSlug, episodeId);
      setOptimisticLikedEpisodes((prev) => new Set(prev).add(key));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  // Helper functions
  const isTopicFollowed = useCallback(
    (topicSlug: string) => optimisticFollowedTopics.has(topicSlug),
    [optimisticFollowedTopics]
  );

  const isEpisodeLiked = useCallback(
    (topicSlug: string, episodeId: string) =>
      optimisticLikedEpisodes.has(makeEpisodeKey(topicSlug, episodeId)),
    [optimisticLikedEpisodes]
  );

  const isTopicPending = useCallback(
    (topicSlug: string) => pendingTopics.has(topicSlug),
    [pendingTopics]
  );

  const isEpisodePending = useCallback(
    (topicSlug: string, episodeId: string) =>
      pendingEpisodes.has(makeEpisodeKey(topicSlug, episodeId)),
    [pendingEpisodes]
  );

  const toggleTopicFollow = useCallback(
    (topicSlug: string) => {
      if (!isAuthenticated) return;
      if (isTopicFollowed(topicSlug)) {
        unfollowMutation.mutate(topicSlug);
      } else {
        followMutation.mutate(topicSlug);
      }
    },
    [isAuthenticated, isTopicFollowed, followMutation, unfollowMutation]
  );

  const toggleEpisodeLike = useCallback(
    (topicSlug: string, episodeId: string) => {
      if (!isAuthenticated) return;
      if (isEpisodeLiked(topicSlug, episodeId)) {
        unlikeMutation.mutate({ topicSlug, episodeId });
      } else {
        likeMutation.mutate({ topicSlug, episodeId });
      }
    },
    [isAuthenticated, isEpisodeLiked, likeMutation, unlikeMutation]
  );

  return {
    isLoading,
    isTopicFollowed,
    isEpisodeLiked,
    isTopicPending,
    isEpisodePending,
    toggleTopicFollow,
    toggleEpisodeLike,
  };
}
